/**
 * Usernames (@handles): unique per workspace (case-insensitive, stored lowercase), 2–32 of a–z, 0–9, ".", "_", "-",
 * starting and ending with a letter or digit, never a reserved word (@here, @channel…). The server assigns one when
 * a member has none (from the email's local part or the name, numbered when taken); people can rename. Mentions
 * resolve to the user id, so renaming never breaks old messages. Pure; shared by the API and every client.
 */
import { msg, type MessageDescriptor } from "../i18n.ts";
import { fold } from "./search.ts";

export const HANDLE_MIN = 2;
export const HANDLE_MAX = 32;

/** Words that already mean something after "@", or that would impersonate the app or its staff. */
export const RESERVED_HANDLES: string[] = [
  "here", "channel", "everyone", "all", "admin", "admins", "administrator", "owner", "owners", "bundle", "system",
  "support", "help", "staff", "team", "moderator", "security", "settings", "api", "me", "you", "anyone", "nobody",
  "guest", "guests", "agent", "agents", "bot", "bots", "root", "null", "undefined",
];

export type HandleProblem = "tooShort" | "tooLong" | "characters" | "edges" | "reserved" | "taken";

const FALLBACK = "member";

function isAlnum(ch: string): boolean {
  return (ch >= "a" && ch <= "z") || (ch >= "0" && ch <= "9");
}

function isPunct(ch: string): boolean {
  return ch === "." || ch === "_" || ch === "-";
}

/** As typed → stored form: trimmed, without leading "@", lowercase. */
export function normalizeHandle(raw: string): string {
  let s = raw.trim();
  while (s.startsWith("@")) s = s.slice(1);
  return s.toLowerCase();
}

/** Why `handle` (normalized) can't be a username, or undefined. `taken` is the server's call. */
export function handleProblem(handle: string): HandleProblem | undefined {
  for (let i = 0; i < handle.length; i++) {
    const ch = handle.charAt(i);
    if (!isAlnum(ch) && !isPunct(ch)) return "characters";
  }
  if (handle.length < HANDLE_MIN) return "tooShort";
  if (handle.length > HANDLE_MAX) return "tooLong";
  if (isPunct(handle.charAt(0)) || isPunct(handle.charAt(handle.length - 1))) return "edges";
  if (RESERVED_HANDLES.includes(handle)) return "reserved";
  return undefined;
}

export function isValidHandle(handle: string): boolean {
  return handleProblem(handle) === undefined;
}

export function handleProblemMessage(p: HandleProblem): MessageDescriptor {
  switch (p) {
    case "tooShort":
      return msg("handle.problem.tooShort");
    case "tooLong":
      return msg("handle.problem.tooLong");
    case "characters":
      return msg("handle.problem.characters");
    case "edges":
      return msg("handle.problem.edges");
    case "reserved":
      return msg("handle.problem.reserved");
    case "taken":
      return msg("handle.problem.taken");
  }
}

function trimPunct(s: string): string {
  let a = 0;
  let b = s.length;
  while (a < b && isPunct(s.charAt(a))) a++;
  while (b > a && isPunct(s.charAt(b - 1))) b--;
  return s.slice(a, b);
}

/** "Ana Souza" → "ana.souza", "José_Ávila!" → "jose_avila": allowed characters only, one punctuation in a row. */
export function slugifyHandle(text: string): string {
  const folded = fold(text).trim();
  let out = "";
  for (const ch of folded) {
    const c = ch === " " || ch === "\t" ? "." : ch;
    if (isAlnum(c)) out += c;
    else if (isPunct(c) && out.length > 0 && !isPunct(out.charAt(out.length - 1))) out += c;
  }
  return trimPunct(trimPunct(out).slice(0, HANDLE_MAX));
}

/**
 * The default username for someone: the email's local part (before "+"), else the name; "member" when neither gives
 * a valid, unreserved one.
 */
export function handleBase(email: string | undefined, name: string): string {
  const candidates: string[] = [];
  if (email !== undefined && email.includes("@")) {
    const local = email.slice(0, email.indexOf("@"));
    const plus = local.indexOf("+");
    candidates.push(slugifyHandle(plus < 0 ? local : local.slice(0, plus)));
  }
  candidates.push(slugifyHandle(name));
  for (const c of candidates) if (isValidHandle(c)) return c;
  return FALLBACK;
}

/** `base`, or the first free "base2", "base3"… (shortened to fit). `taken` is compared case-insensitively. */
export function uniqueHandle(base: string, taken: string[]): string {
  const used = new Set(taken.map((t) => t.toLowerCase()));
  const free = (h: string) => !used.has(h) && isValidHandle(h);
  if (free(base)) return base;
  for (let n = 2; ; n++) {
    const suffix = String(n);
    const candidate = `${trimPunct(base.slice(0, HANDLE_MAX - suffix.length))}${suffix}`;
    if (free(candidate)) return candidate;
  }
}

/** Free alternatives when `desired` is taken: numbered variants, then one from the name. */
export function handleSuggestions(desired: string, taken: string[], name: string, limit: number): string[] {
  const used = new Set(taken.map((t) => t.toLowerCase()));
  const out: string[] = [];
  const base = isValidHandle(desired) ? desired : handleBase(undefined, desired);
  const fromName = slugifyHandle(name);
  if (fromName !== base && isValidHandle(fromName) && !used.has(fromName)) out.push(fromName);
  for (let n = 2; out.length < limit && n < 100; n++) {
    const suffix = String(n);
    const candidate = `${trimPunct(base.slice(0, HANDLE_MAX - suffix.length))}${suffix}`;
    if (!used.has(candidate) && isValidHandle(candidate) && !out.includes(candidate)) out.push(candidate);
  }
  return out.slice(0, Math.max(0, limit));
}

/** "@ada.l". */
export function formatHandle(handle: string): string {
  return `@${handle}`;
}
